export interface ThemeDef {
  bgColor: string;
  primaryColor: string;
  accentColor: string;
  textColor: string;
  darkBg: boolean;
}

export function SlideMockup({ slideIndex, theme }: { slideIndex: number; theme: ThemeDef }) {
  const { primaryColor, accentColor, textColor, darkBg } = theme;
  const mutedText   = darkBg ? 'rgba(255,255,255,0.28)' : 'rgba(0,0,0,0.18)';
  const subtleText  = darkBg ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.40)';

  const Bar = ({ w, color, h = '5px' }: { w: string; color: string; h?: string }) => (
    <div style={{ width: w, height: h, backgroundColor: color, borderRadius: '3px', flexShrink: 0 }} />
  );

  const layouts: JSX.Element[] = [
    // 0: Title Slide
    <div key="title" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10%', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '28%', backgroundColor: primaryColor, opacity: 0.14 }} />
      <div style={{ width: '5px', height: '32%', backgroundColor: primaryColor, borderRadius: '3px', position: 'absolute', left: '8%' }} />
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10%', zIndex: 1 }}>
        <Bar w="52%" color={primaryColor} h="5px" />
        <Bar w="36%" color={textColor}    h="3px" />
        <div style={{ marginTop: '6%' }}><Bar w="26%" color={mutedText} h="2px" /></div>
      </div>
    </div>,

    // 1: Agenda
    <div key="agenda" style={{ width: '100%', height: '100%', display: 'flex' }}>
      <div style={{ width: '5px', backgroundColor: primaryColor, flexShrink: 0 }} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '9%', padding: '12% 10%' }}>
        <Bar w="46%" color={textColor} h="4px" />
        <div style={{ marginTop: '5%', display: 'flex', flexDirection: 'column', gap: '9%' }}>
          {[1, 2, 3, 4].map(i => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '7%' }}>
              <div style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: primaryColor, flexShrink: 0 }} />
              <div style={{ height: '3px', flex: 1, backgroundColor: i === 1 ? subtleText : mutedText, borderRadius: '2px' }} />
            </div>
          ))}
        </div>
      </div>
    </div>,

    // 2: Executive Summary
    <div key="exec" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ height: '22%', backgroundColor: primaryColor, display: 'flex', alignItems: 'center', padding: '0 10%', flexShrink: 0 }}>
        <Bar w="38%" color="rgba(255,255,255,0.75)" h="3px" />
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '9%', padding: '8% 10%' }}>
        {[80, 62, 70].map((w, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6%' }}>
            <div style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: accentColor, flexShrink: 0 }} />
            <div style={{ height: '3px', width: `${w}%`, backgroundColor: i === 0 ? subtleText : mutedText, borderRadius: '2px' }} />
          </div>
        ))}
      </div>
    </div>,

    // 3: Market Overview (bar chart)
    <div key="market" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '8% 10% 4%', flexShrink: 0 }}><Bar w="36%" color={textColor} h="3px" /></div>
      <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: '3%', padding: '4% 10% 6%' }}>
        {[55, 70, 40, 88, 60].map((h, i) => (
          <div key={i} style={{ flex: 1, height: `${h}%`, backgroundColor: i === 3 ? primaryColor : accentColor, borderRadius: '3px 3px 0 0', opacity: i === 3 ? 1 : 0.45 }} />
        ))}
      </div>
      <div style={{ height: '1px', backgroundColor: mutedText, margin: '0 10% 6%' }} />
    </div>,

    // 4: Problem Statement
    <div key="problem" style={{ width: '100%', height: '100%', display: 'flex' }}>
      <div style={{ width: '44%', backgroundColor: primaryColor, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '8%', gap: '14%', flexShrink: 0 }}>
        <div style={{ width: '22px', height: '22px', borderRadius: '50%', border: '2px solid rgba(255,255,255,0.5)' }} />
        <Bar w="75%" color="rgba(255,255,255,0.65)" h="2px" />
        <Bar w="58%" color="rgba(255,255,255,0.38)" h="2px" />
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '10%', padding: '8% 10%' }}>
        <Bar w="70%" color={textColor}  h="3px" />
        <Bar w="55%" color={mutedText}  h="2px" />
        <Bar w="65%" color={mutedText}  h="2px" />
      </div>
    </div>,

    // 5: Our Solution (3 cards)
    <div key="solution" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '8% 10% 4%', flexShrink: 0 }}><Bar w="40%" color={textColor} h="3px" /></div>
      <div style={{ flex: 1, display: 'flex', gap: '4%', padding: '0 10% 8%' }}>
        {[0, 1, 2].map(i => (
          <div key={i} style={{ flex: 1, border: `1.5px solid ${i === 1 ? primaryColor : mutedText}`, borderRadius: '6px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: '8%', padding: '8% 6%', backgroundColor: i === 1 ? primaryColor + '14' : 'transparent' }}>
            <div style={{ width: '12px', height: '12px', borderRadius: '4px', backgroundColor: primaryColor, opacity: i === 1 ? 1 : 0.38 }} />
            <div style={{ height: '2px', width: '70%', backgroundColor: i === 1 ? subtleText : mutedText, borderRadius: '2px' }} />
            <div style={{ height: '2px', width: '52%', backgroundColor: mutedText, borderRadius: '2px' }} />
          </div>
        ))}
      </div>
    </div>,

    // 6: Data & Stats
    <div key="data" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '7%', position: 'relative' }}>
      <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(ellipse at center, ${primaryColor}18 0%, transparent 65%)` }} />
      <div style={{ fontSize: '240%', fontWeight: 900, color: primaryColor, lineHeight: 1 }}>87%</div>
      <Bar w="40%" color={mutedText} h="2px" />
      <div style={{ display: 'flex', gap: '8%' }}>
        {['24M', '3.2x', '99%'].map((v, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
            <div style={{ fontSize: '105%', fontWeight: 800, color: accentColor, opacity: 0.8 }}>{v}</div>
            <div style={{ width: '16px', height: '2px', backgroundColor: mutedText, borderRadius: '2px' }} />
          </div>
        ))}
      </div>
    </div>,

    // 7: Case Study
    <div key="case" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ height: '20%', backgroundColor: primaryColor + '1a', display: 'flex', alignItems: 'center', padding: '0 8%', flexShrink: 0, borderBottom: `1.5px solid ${primaryColor}25` }}>
        <Bar w="33%" color={textColor} h="2px" />
      </div>
      <div style={{ flex: 1, display: 'flex' }}>
        {['Before', 'After'].map((label, i) => (
          <div key={label} style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '10%', padding: '8%', backgroundColor: i === 1 ? primaryColor + '0c' : 'transparent', borderRight: i === 0 ? `1px solid ${mutedText}` : 'none' }}>
            <div style={{ fontSize: '70%', fontWeight: 700, color: i === 1 ? primaryColor : mutedText, textTransform: 'uppercase' as const }}>{label}</div>
            <Bar w="80%" color={i === 1 ? primaryColor + '90' : mutedText} h="2px" />
            <Bar w="62%" color={mutedText} h="2px" />
          </div>
        ))}
      </div>
    </div>,

    // 8: Roadmap
    <div key="roadmap" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '10%', gap: '10%' }}>
      <Bar w="38%" color={textColor} h="3px" />
      <div style={{ display: 'flex', alignItems: 'center', position: 'relative', marginTop: '4%' }}>
        <div style={{ position: 'absolute', left: 0, right: 0, height: '2px', backgroundColor: primaryColor + '35', top: '50%', transform: 'translateY(-50%)' }} />
        {[0, 1, 2, 3].map(i => (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', position: 'relative', zIndex: 1 }}>
            <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: i <= 1 ? primaryColor : accentColor + '55', border: `1.5px solid ${primaryColor}`, boxShadow: i === 1 ? `0 0 0 3px ${primaryColor}28` : 'none' }} />
            <div style={{ width: '28px', height: '2px', backgroundColor: mutedText, borderRadius: '2px' }} />
          </div>
        ))}
      </div>
    </div>,

    // 9: Team
    <div key="team" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '8% 10% 4%', flexShrink: 0 }}><Bar w="36%" color={textColor} h="3px" /></div>
      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '5%', padding: '4% 10% 8%' }}>
        {[0, 1, 2, 3].map(i => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
            <div style={{ width: '28px', height: '28px', borderRadius: '50%', backgroundColor: primaryColor, opacity: 0.65 + (i * 0.1) }} />
            <div style={{ height: '2px', width: '70%', backgroundColor: mutedText, borderRadius: '2px' }} />
            <div style={{ height: '2px', width: '52%', backgroundColor: mutedText, borderRadius: '2px', opacity: 0.5 }} />
          </div>
        ))}
      </div>
    </div>,

    // 10: Financials
    <div key="fin" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '8% 10% 2%', flexShrink: 0 }}><Bar w="36%" color={textColor} h="3px" /></div>
      <div style={{ flex: 1, padding: '2% 10% 8%' }}>
        <svg width="100%" height="100%" viewBox="0 0 100 60" preserveAspectRatio="none">
          <defs>
            <linearGradient id={`fg-${primaryColor.replace('#','')}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor={primaryColor} stopOpacity="0.35" />
              <stop offset="100%" stopColor={primaryColor} stopOpacity="0.02" />
            </linearGradient>
          </defs>
          <path d="M5 50 C15 45,20 35,30 30 C40 25,50 38,60 25 C70 12,80 8,95 5" fill="none" stroke={primaryColor} strokeWidth="2.5" strokeLinecap="round" />
          <path d="M5 50 C15 45,20 35,30 30 C40 25,50 38,60 25 C70 12,80 8,95 5 L95 60 L5 60 Z" fill={`url(#fg-${primaryColor.replace('#','')})`} />
          <line x1="5" y1="60" x2="95" y2="60" stroke={mutedText} strokeWidth="0.6" />
        </svg>
      </div>
    </div>,

    // 11: Next Steps (CTA)
    <div key="next" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8%', position: 'relative' }}>
      <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(135deg, ${primaryColor}1a 0%, transparent 60%)` }} />
      <Bar w="44%" color={textColor} h="3px" />
      <Bar w="30%" color={mutedText}  h="2px" />
      <div style={{ padding: '6% 20%', backgroundColor: primaryColor, borderRadius: '20px', marginTop: '5%' }}>
        <Bar w="100%" color="rgba(255,255,255,0.82)" h="3px" />
      </div>
    </div>,
  ];

  return <div style={{ width: '100%', height: '100%' }}>{layouts[slideIndex] ?? layouts[0]}</div>;
}
